import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import api, { complaintsAPI } from "../../services/api";
import {
  PlusIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import ComplaintsStatistics from "../../components/complaints/ComplaintsStatistics";
import ComplaintsOverview from "../../components/complaints/ComplaintsOverview";
import ComplaintsTable from "../../components/complaints/ComplaintsTable";
import ComplaintFormModal from "../../components/complaints/modals/ComplaintFormModal";
import ViewComplaintModal from "../../components/complaints/modals/ViewComplaintModal";
import DeleteConfirmationModal from "../../components/complaints/modals/DeleteConfirmationModal";
import ComplaintsCategoriesModal from "../../components/complaints/modals/ComplaintsCategoriesModal";

const ComplaintsPage = () => {
  const [complaints, setComplaints] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [residentSearchQuery, setResidentSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);
  const [showResidentSearch, setShowResidentSearch] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [complaintToDelete, setComplaintToDelete] = useState(null);
  const [showOverview, setShowOverview] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);

  // State for filter options - these will contain ALL possible values
  const [filterOptions, setFilterOptions] = useState({
    puroks: [],
    types: [],
    statuses: [],
    priorities: []
  });
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    purok: "",
    type: "",
    status: "",
    priority: "",
    date_from: "",
    date_to: "",
    search: "",
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  // Fetch ALL possible filter options on component mount
  const fetchAllFilterOptions = async () => {
    try {
      setLoadingOptions(true);
      // Fetch a large number of records to get all possible values
      const response = await complaintsAPI.getAll({
        per_page: 1000 // Get up to 1000 records
      });
      
      let allComplaints = [];
      
      // Extract complaints from the response based on your API structure
      if (response?.data?.data?.complaints && Array.isArray(response.data.data.complaints)) {
        allComplaints = response.data.data.complaints;
      }
      else if (response?.data?.complaints && Array.isArray(response.data.complaints)) {
        allComplaints = response.data.complaints;
      }
      else if (response?.data?.data && Array.isArray(response.data.data)) {
        allComplaints = response.data.data;
      }
      else if (Array.isArray(response?.data)) {
        allComplaints = response.data;
      }
      
      // Extract unique values from ALL complaints
      const puroks = [...new Set(allComplaints
        .map(c => c.purok)
        .filter(p => p && p !== 'N/A' && p !== '')
      )].sort();
      
      const types = [...new Set(allComplaints
        .map(c => {
          // Handle different possible structures for complaint type/category
          if (c.complaint_type) {
            if (typeof c.complaint_type === 'string') return c.complaint_type;
            if (typeof c.complaint_type === 'object' && c.complaint_type?.name) return c.complaint_type.name;
          }
          if (c.category) {
            if (typeof c.category === 'string') return c.category;
            if (typeof c.category === 'object' && c.category?.name) return c.category.name;
          }
          if (c.type) {
            if (typeof c.type === 'string') return c.type;
            if (typeof c.type === 'object' && c.type?.name) return c.type.name;
          }
          return null;
        })
        .filter(t => t && t !== 'N/A' && t !== '')
      )].sort();
      
      const statuses = [...new Set(allComplaints
        .map(c => c.status)
        .filter(s => s && s !== '')
      )].sort();
      
      const priorities = [...new Set(allComplaints
        .map(c => c.priority)
        .filter(p => p && p !== '')
      )].sort();
      
      setFilterOptions({
        puroks,
        types,
        statuses,
        priorities
      });
    } catch (error) {
      console.error("Error fetching filter options:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
      };

      if (filters.search) params.search = filters.search;
      if (filters.purok) params.purok = filters.purok;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      // console.log("Fetching complaints with params:", params);
      
      const response = await complaintsAPI.getAll(params);
      // console.log("API Response:", response);
      
      let complaintsData = [];
      let total = 0;
      let lastPage = 1;
      
      if (response?.data?.data?.complaints && Array.isArray(response.data.data.complaints)) {
        complaintsData = response.data.data.complaints;
        if (response.data.data.meta) {
          total = response.data.data.meta.total || 0;
          lastPage = response.data.data.meta.last_page || 1;
        }
      }
      else if (response?.data?.complaints && Array.isArray(response.data.complaints)) {
        complaintsData = response.data.complaints;
        if (response.data.meta) {
          total = response.data.meta.total || 0;
          lastPage = response.data.meta.last_page || 1;
        }
      }
      else if (response?.data?.data && Array.isArray(response.data.data)) {
        complaintsData = response.data.data;
        total = complaintsData.length;
        lastPage = 1;
      }
      else if (Array.isArray(response?.data)) {
        complaintsData = response.data;
        total = complaintsData.length;
        lastPage = 1;
      }
      
      setComplaints(complaintsData);
      setTotalItems(total);
      setTotalPages(lastPage);
      
    } catch (error) {
      console.error("Error fetching complaints:", error);
      setComplaints([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get("/complaints/statistics");
      setStatistics(response.data?.data || response.data || {});
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setStatistics({});
    }
  };

  const refreshAllData = async () => {
    await Promise.all([
      fetchComplaints(),
      fetchStatistics(),
      fetchAllFilterOptions() // Refresh filter options when data changes
    ]);
  };

  const searchResidents = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await api.get("/complaints/search-residents", {
        params: { search: query },
      });
      setSearchResults(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Error searching residents:", error);
      setSearchResults([]);
    }
  };

  // Fetch filter options on component mount
  useEffect(() => {
    fetchAllFilterOptions();
  }, []);

  useEffect(() => {
    fetchComplaints();
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const handleResidentSelect = (resident) => {
    setSelectedResident(resident);
    setValue("resident_id", resident.id);
    setValue(
      "complainant_name",
      `${resident.first_name} ${resident.last_name}`,
    );
    setValue("complainant_contact", resident.phone_number || resident.email);
    setShowResidentSearch(false);
    setResidentSearchQuery(`${resident.first_name} ${resident.last_name}`);
  };

  const handleManualEntry = () => {
    setIsManualEntry(true);
    setSelectedResident(null);
    setValue("resident_id", "");
    setValue("complainant_name", "");
    setValue("complainant_contact", "");
    setShowResidentSearch(false);
    setResidentSearchQuery("");
  };

  const resetComplaintForm = () => {
    setShowForm(false);
    setSelectedComplaint(null);
    setSelectedResident(null);
    setIsManualEntry(false);
    setShowResidentSearch(false);
    setResidentSearchQuery("");
    setSearchResults([]);
    setIsSubmitting(false);
    reset();
  };

  const handleSubmitComplaint = async (data) => {
    try {
      setIsSubmitting(true);

      const submitData = {
        ...data,
        resident_id: selectedResident?.id || null,
      };

      if (selectedComplaint) {
        await api.put(`/complaints/${selectedComplaint.id}`, submitData);
      } else {
        await api.post("/complaints", submitData);
      }

      resetComplaintForm();
      
      await refreshAllData();
      
    } catch (error) {
      console.error("Error saving complaint:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComplaint = async (id) => {
    if (!id) return;
    
    try {
      await api.delete(`/complaints/${id}`);

      setComplaints((prevComplaints) =>
        prevComplaints.filter((complaint) => complaint.id !== id),
      );

      const deletedComplaint = complaints.find((c) => c.id === id);
      if (deletedComplaint) {
        setStatistics((prevStats) => ({
          ...prevStats,
          total_complaints: Math.max(0, (prevStats.total_complaints || 0) - 1),
          [`total_${deletedComplaint.status}_complaints`]: Math.max(
            0,
            (prevStats[`total_${deletedComplaint.status}_complaints`] || 0) - 1,
          ),
        }));
      }

      if (selectedComplaint && selectedComplaint.id === id) {
        setSelectedComplaint(null);
      }

      setShowDeleteModal(false);
      setComplaintToDelete(null);
      
      await refreshAllData();
      
    } catch (error) {
      console.error("Error deleting complaint:", error);
      await refreshAllData();
      setShowDeleteModal(false);
      setComplaintToDelete(null);
    }
  };

  const confirmDelete = (complaint) => {
    setComplaintToDelete(complaint);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setComplaintToDelete(null);
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.patch(`/complaints/${id}/status`, { status });

      setComplaints((prevComplaints) =>
        prevComplaints.map((complaint) =>
          complaint.id === id ? { ...complaint, status } : complaint,
        ),
      );

      if (selectedComplaint && selectedComplaint.id === id) {
        setSelectedComplaint({ ...selectedComplaint, status });
      }

      const updatedComplaint = complaints.find(c => c.id === id);
      if (updatedComplaint && updatedComplaint.status !== status) {
        setStatistics(prevStats => ({
          ...prevStats,
          [`total_${status}_complaints`]: (prevStats[`total_${status}_complaints`] || 0) + 1,
          [`total_${updatedComplaint.status}_complaints`]: Math.max(0, (prevStats[`total_${updatedComplaint.status}_complaints`] || 0) - 1)
        }));
      }

      await refreshAllData();
      
    } catch (error) {
      console.error("Error updating status:", error);
      await refreshAllData();
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: "bg-blue-100 text-blue-800",
      acknowledged: "bg-yellow-100 text-yellow-800",
      assigned: "bg-purple-100 text-purple-800",
      "in progress": "bg-indigo-100 text-indigo-800",
      in_progress: "bg-indigo-100 text-indigo-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800",
      dismissed: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "bg-green-100 text-green-800",
      medium: "bg-yellow-100 text-yellow-800",
      high: "bg-orange-100 text-orange-800",
      urgent: "bg-red-100 text-red-800",
    };
    return colors[priority] || "bg-gray-100 text-gray-800";
  };

  const toggleOverview = () => {
    setShowOverview(!showOverview);
  };

  const handleViewComplaint = (complaint) => {
    setSelectedComplaint(complaint);
  };

  const handleEditComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    reset({
      ...complaint,
      resident_id: complaint.resident?.id || complaint.resident_id,
      complainant_name: complaint.complainant_name || complaint.resident?.name,
      complainant_contact: complaint.complainant_contact || complaint.resident?.phone_number,
    });
    
    if (complaint.resident) {
      setSelectedResident(complaint.resident);
      setResidentSearchQuery(complaint.resident.name);
    }
    
    setShowForm(true);
    setIsSubmitting(false);
  };

  const handleOpenNewComplaint = () => {
    setShowForm(true);
    setSelectedComplaint(null);
    setSelectedResident(null);
    setIsManualEntry(false);
    setShowResidentSearch(false);
    setResidentSearchQuery("");
    setSearchResults([]);
    setIsSubmitting(false);
    reset();
  };

  const handleCloseViewModal = () => {
    setSelectedComplaint(null);
  };

  const handleDeleteFromView = (complaint) => {
    setSelectedComplaint(null);
    confirmDelete(complaint);
  };

  const handleComplaintsCategoriesClick = () => {
    setShowCategoriesModal(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleComplaintCreated = () => {
    refreshAllData();
  };

  const handleComplaintUpdated = () => {
    refreshAllData();
  };

  const getComplainantName = (complaint) => {
    if (!complaint) return 'Unknown';
    
    if (complaint.resident) {
      if (typeof complaint.resident === 'string') {
        return complaint.resident;
      }
      if (typeof complaint.resident === 'object' && complaint.resident.name) {
        return complaint.resident.name;
      }
    }
    
    if (complaint.complainant_name) {
      return complaint.complainant_name;
    }
    
    return complaint.is_anonymous ? 'Anonymous' : 'Unknown';
  };

  const getComplaintType = (complaint) => {
    if (!complaint) return 'Uncategorized';
    
    if (complaint.complaint_type) {
      if (typeof complaint.complaint_type === 'string') {
        return complaint.complaint_type;
      }
      if (typeof complaint.complaint_type === 'object' && complaint.complaint_type !== null) {
        return complaint.complaint_type.name || complaint.complaint_type.type_name || 'Uncategorized';
      }
    }
    
    if (complaint.category) {
      if (typeof complaint.category === 'string') {
        return complaint.category;
      }
      if (typeof complaint.category === 'object' && complaint.category !== null) {
        return complaint.category.name || 'Uncategorized';
      }
    }
    
    if (complaint.type) {
      if (typeof complaint.type === 'string') {
        return complaint.type;
      }
      if (typeof complaint.type === 'object' && complaint.type !== null) {
        return complaint.type.name || 'Uncategorized';
      }
    }
    
    return 'Uncategorized';
  };

  if (loading && complaints.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-9xl mx-auto p-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Complaints</h1>
              <p className="text-gray-600">
                Manage and track resident complaints efficiently
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Complaints Categories Button */}
            <button
              onClick={handleComplaintsCategoriesClick}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              style={{ backgroundColor: "#DBEAFE", color: "#1E3A8A" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                style={{ color: "#2563EB" }}
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2" />
              </svg>
              <span className="font-medium text-sm">Complaints Categories</span>
            </button>

            <button
              onClick={handleOpenNewComplaint}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Complaint
            </button>
          </div>
        </div>

        {/* Statistics Section */}
        <ComplaintsStatistics statistics={statistics} />

        {/* Overview Section */}
        <ComplaintsOverview
          statistics={statistics}
          complaints={complaints}
          showDetails={showOverview}
          toggleDetails={toggleOverview}
        />

        {/* Table Section */}
        <ComplaintsTable
          complaints={complaints}
          loading={loading}
          onView={handleViewComplaint}
          onEdit={handleEditComplaint}
          onDelete={confirmDelete}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          getComplainantName={getComplainantName}
          getComplaintType={getComplaintType}
          
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearchChange={handleSearchChange}
          
          // Pass ALL possible filter options (not just from filtered results)
          purokOptions={filterOptions.puroks}
          typeOptions={filterOptions.types}
          statusOptions={filterOptions.statuses}
          priorityOptions={filterOptions.priorities}
          
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />

        {/* Form Modal */}
        <ComplaintFormModal
          isOpen={showForm}
          onClose={resetComplaintForm}
          onSuccess={refreshAllData}
          onComplaintCreated={handleComplaintCreated}
          onComplaintUpdated={handleComplaintUpdated}
          selectedComplaint={selectedComplaint}
          register={register}
          handleSubmit={handleSubmit}
          errors={errors}
          isSubmitting={isSubmitting}
          residentSearchQuery={residentSearchQuery}
          setResidentSearchQuery={setResidentSearchQuery}
          searchResidents={searchResidents}
          searchResults={searchResults}
          handleResidentSelect={handleResidentSelect}
          showResidentSearch={showResidentSearch}
          setShowResidentSearch={setShowResidentSearch}
          selectedResident={selectedResident}
          isManualEntry={isManualEntry}
          setIsManualEntry={setIsManualEntry}
          handleManualEntry={handleManualEntry}
          setValue={setValue}
          reset={reset}
        />

        {/* View Modal */}
        <ViewComplaintModal
          isOpen={!!selectedComplaint && !showForm}
          onClose={handleCloseViewModal}
          complaint={selectedComplaint}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          onUpdateStatus={handleUpdateStatus}
          onDelete={handleDeleteFromView}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={() => handleDeleteComplaint(complaintToDelete?.id)}
          complaint={complaintToDelete}
        />

        {/* Categories Modal */}
        <ComplaintsCategoriesModal
          isOpen={showCategoriesModal}
          onClose={() => setShowCategoriesModal(false)}
        />
      </div>
    </div>
  );
};

export default ComplaintsPage;